import React from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../atoms/Spinner";
import { CustomButton } from "../../../atoms/CustomButton";
import { useGetMemberSettings } from "../../../../hooks/agenda";
import AvailabilityText from "../agenda/AvailabilityText";

interface AvailabilityLineProps {
  customerIdentification: string;
  entityIdentification: string;
  userIdentification: string;
}

/** A person's weekly availability at a glance, under the title of their appointments. */
const AvailabilityLine: React.FC<AvailabilityLineProps> = ({
  customerIdentification,
  entityIdentification,
  userIdentification,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useGetMemberSettings(
    customerIdentification,
    entityIdentification,
    userIdentification
  );

  let value: React.ReactNode;
  if (isLoading) {
    value = <Spinner size="sm" />;
  } else if (isError) {
    value = (
      <span className="text-red-600">
        {t("agenda.settings.loadError")}{" "}
        <CustomButton
          variant="none"
          size="xs"
          className="inline text-blue-600 underline hover:text-blue-800"
          comp={t("agenda.retry")}
          onClick={() => refetch()}
        />
      </span>
    );
  } else if (!data) {
    value = (
      <span className="text-gray-600">{t("agenda.availability.noneLine")}</span>
    );
  } else {
    value = <AvailabilityText settings={data} />;
  }

  return (
    <dl className="grid grid-cols-1 gap-x-3 gap-y-0.5 border-b border-gray-100 py-3 text-sm sm:grid-cols-[auto_minmax(0,1fr)]">
      <dt className="font-medium text-gray-500">
        {t("agenda.availability.title")}
      </dt>
      <dd className="min-w-0 break-words text-gray-900">{value}</dd>
    </dl>
  );
};

export default AvailabilityLine;
